import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, Users, Sparkles, DollarSign, Clock, ArrowRight, RotateCcw, Filter, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const AUD = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  // Treat null, undefined, empty, and unparseable inputs as "not recorded".
  // `new Date(null)` would otherwise render the Unix epoch ("1 Jan 1970"),
  // which is a meaningless leak of internal state to the admin UI.
  if (iso === null || iso === undefined || iso === "" || iso === 0 || iso === "0") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};
const fmtHours = (h) => {
  if (!h) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

function Stat({ icon: Icon, label, value, sub, testId, accent = "text-[#9A7B4F]" }) {
  return (
    <div className="bg-white border border-[#282828]/8 p-4" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 flex items-center justify-center bg-[#FAF8F0] ${accent}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/60">{label}</div>
          <div className="text-[22px] text-[#282828] font-medium tabular-nums" data-testid={`${testId}-value`}>{value}</div>
          {sub && <div className="text-[10.5px] text-[#676664]/60 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function FunnelBar({ label, count, max, color, testId }) {
  const pct = max > 0 ? Math.max(2, (count / max) * 100) : 2;
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="w-24 text-[11px] text-[#676664] uppercase tracking-wider flex-shrink-0">{label}</div>
      <div className="flex-1 bg-[#FAF8F0] h-7 relative overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
        <div className="absolute inset-0 flex items-center px-2 text-[11px] text-[#282828] font-medium">
          {count}
        </div>
      </div>
    </div>
  );
}

function TrendChart({ data }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.enquired, d.converted]));
  return (
    <div data-testid="trend-chart">
      <div className="flex justify-between mb-2 text-[10px] uppercase tracking-wider text-[#676664]/60">
        <span>12-week trend</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#D4B8B4]" />Enquired</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#9A7B4F]" />Converted</span>
        </span>
      </div>
      <div className="flex items-end gap-1 h-32 border-b border-[#282828]/10">
        {data.map((d, i) => {
          const eH = (d.enquired / max) * 100;
          const cH = (d.converted / max) * 100;
          return (
            <div key={d.week} className="flex-1 flex items-end gap-0.5 h-full" title={`${d.week}\nEnquired: ${d.enquired}\nConverted: ${d.converted}`}>
              <div className="flex-1 bg-[#D4B8B4] transition-all" style={{ height: `${eH}%` }} />
              <div className="flex-1 bg-[#9A7B4F] transition-all" style={{ height: `${cH}%` }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-[#676664]/50 mt-1">
        <span>{data[0]?.week}</span>
        <span>{data[data.length - 1]?.week}</span>
      </div>
    </div>
  );
}

export default function AdminTrialAnalytics() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    setLoadError(null);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/analytics/trials`, { params, headers: auth() }),
        axios.get(`${API}/admin/analytics/trials/recent-conversions?limit=20`, { headers: auth() }),
      ]);
      setData(s.data || {});
      setRecent(r.data?.items || []);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || err.message || "Network error";
      const shown = status ? `HTTP ${status}: ${msg}` : msg;
      console.error("[AdminTrialAnalytics] load failed", { status, detail, err });
      setLoadError(shown);
      toast.error(`Could not load trial analytics — ${shown}`);
    }
    setRefreshing(false);
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Always render inside AdminLayout so sidebar is visible in every state.
  const summary = data?.summary || {};
  const funnel = data?.funnel || {};
  const bySource = data?.by_source || [];
  const trend = data?.trend || [];
  const funnelMax = Math.max(funnel.enquired || 0, funnel.contacted || 0, funnel.booked || 0, funnel.converted || 0, 1);
  const hasAnyData = (summary.total_leads || 0) > 0 || recent.length > 0;

  return (
    <AdminLayout
      title="Trial Analytics"
      subtitle="Trial enquiry → rental conversion funnel"
      actions={
        <Button variant="outline" onClick={fetchAll} disabled={refreshing} className="text-[11px] h-8 px-4" data-testid="ta-refresh">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
          Refresh
        </Button>
      }
    >
      {loading && (
        <div className="flex justify-center items-center py-20" data-testid="ta-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
        </div>
      )}

      {!loading && loadError && (
        <div className="bg-white border border-red-200 p-6 text-center" data-testid="ta-error">
          <div className="text-[13px] text-red-700 font-medium mb-1">Could not load Trial Analytics</div>
          <div className="text-[11px] text-[#676664] mb-3 font-mono break-all" data-testid="ta-error-msg">{loadError}</div>
          <Button onClick={fetchAll} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="ta-retry">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Retry
          </Button>
        </div>
      )}

      {!loading && !loadError && (
        <>
      {/* Date range filter */}
      <div className="bg-white border border-[#282828]/8 p-3 mb-4 flex items-end gap-2 flex-wrap" data-testid="ta-filters">
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="ta-from" />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider text-[#676664]/60 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-[#282828]/12 px-2 py-1.5 text-[11px]" data-testid="ta-to" />
        </div>
        <Button variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); setTimeout(fetchAll, 0); }} className="text-[11px] h-8 px-3" data-testid="ta-reset">Reset</Button>
        <Button onClick={fetchAll} disabled={refreshing} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="ta-apply">
          <Filter className="w-3.5 h-3.5 mr-1" /> Apply
        </Button>
        <div className="flex-1" />
        <Link to="/admin/trial-leads" className="text-[11px] text-[#9A7B4F] hover:underline inline-flex items-center gap-1" data-testid="ta-view-leads">
          View all trial leads <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Top stats */}
      {!hasAnyData ? (
        <div className="bg-white border border-[#282828]/8 p-10 text-center" data-testid="ta-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#676664]/20" strokeWidth={1.2} />
          <div className="text-[13px] text-[#282828] font-medium mb-1">No trial analytics data yet</div>
          <div className="text-[11px] text-[#676664]/70 max-w-md mx-auto">Once customers submit trial enquiries and admins convert them to bookings, you'll see funnel + revenue analytics here.</div>
        </div>
      ) : (
        <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
        <Stat icon={Users} label="Total leads" value={summary.total_leads || 0} testId="stat-total-leads" />
        <Stat icon={Sparkles} label="Converted" value={summary.total_converted || 0} testId="stat-converted" accent="text-[#6B8F71]" />
        <Stat icon={TrendingUp} label="Conversion rate" value={`${summary.conversion_rate ?? 0}%`} testId="stat-rate" />
        <Stat icon={DollarSign} label="Revenue" value={AUD(summary.revenue)} sub={summary.revenue_bookings ? `from ${summary.revenue_bookings} booking${summary.revenue_bookings === 1 ? "" : "s"}` : "unlinked"} testId="stat-revenue" accent="text-amber-700" />
        <Stat icon={DollarSign} label="Avg booking value" value={AUD(summary.avg_conversion_value)} testId="stat-avg-value" />
        <Stat icon={Clock} label="Avg time to convert" value={fmtHours(summary.avg_time_to_convert_hours)} testId="stat-time" />
      </div>

      {/* Funnel + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="bg-white border border-[#282828]/8 p-4" data-testid="funnel-block">
          <h3 className="text-[12px] tracking-wider uppercase text-[#676664] mb-3">Conversion funnel</h3>
          <div className="space-y-2.5">
            <FunnelBar label="Enquired" count={funnel.enquired} max={funnelMax} color="bg-[#D4B8B4]/70" testId="funnel-enquired" />
            <FunnelBar label="Contacted" count={funnel.contacted} max={funnelMax} color="bg-[#a855f7]/40" testId="funnel-contacted" />
            <FunnelBar label="Booked" count={funnel.booked} max={funnelMax} color="bg-[#B8C9BD]" testId="funnel-booked" />
            <FunnelBar label="Converted" count={funnel.converted} max={funnelMax} color="bg-[#9A7B4F]" testId="funnel-converted" />
          </div>
          <div className="mt-3 pt-3 border-t border-[#282828]/8 text-[10.5px] text-[#676664]/70">
            Conversion rate: <span className="text-[#282828] font-medium tabular-nums">{summary.conversion_rate ?? 0}%</span> of enquiries became rental bookings.
          </div>
        </div>
        <div className="bg-white border border-[#282828]/8 p-4">
          <TrendChart data={trend} />
        </div>
      </div>

      {/* By source */}
      {bySource.length > 0 && (
        <div className="bg-white border border-[#282828]/8 p-4 mb-5" data-testid="by-source-block">
          <h3 className="text-[12px] tracking-wider uppercase text-[#676664] mb-3">By acquisition source</h3>
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#676664]/60 border-b border-[#282828]/10">
                <th className="text-left pb-1.5">Source</th>
                <th className="text-right pb-1.5">Leads</th>
                <th className="text-right pb-1.5">Converted</th>
                <th className="text-right pb-1.5">Rate</th>
              </tr>
            </thead>
            <tbody>
              {bySource.map((s) => (
                <tr key={s.source} className="border-b border-[#282828]/5 last:border-0" data-testid={`source-${s.source}`}>
                  <td className="py-1.5">{(s.source || "unknown").replace(/_/g, " ")}</td>
                  <td className="py-1.5 text-right tabular-nums">{s.total || 0}</td>
                  <td className="py-1.5 text-right tabular-nums">{s.converted || 0}</td>
                  <td className="py-1.5 text-right tabular-nums text-[#9A7B4F]">{s.conversion_rate ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent conversions */}
      <div className="bg-white border border-[#282828]/8 p-4" data-testid="recent-block">
        <h3 className="text-[12px] tracking-wider uppercase text-[#676664] mb-3">Recent conversions</h3>
        {recent.length === 0 ? (
          <div className="text-center py-10 text-[12px] text-[#676664]/50 italic">No converted trials yet.</div>
        ) : (
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#676664]/60 border-b border-[#282828]/10">
                <th className="text-left pb-1.5">Customer</th>
                <th className="text-left pb-1.5">Trial date</th>
                <th className="text-left pb-1.5">Converted</th>
                <th className="text-left pb-1.5">Bookings</th>
                <th className="text-right pb-1.5">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.lead_id} className="border-b border-[#282828]/5 last:border-0" data-testid={`recent-${r.lead_id}`}>
                  <td className="py-2">
                    <div className="text-[#282828]">{r.name || "—"}</div>
                    <div className="text-[10px] text-[#676664]/60">{r.email}</div>
                  </td>
                  <td className="py-2 text-[#676664]" data-testid={`recent-trial-date-${r.lead_id}`}>
                    {fmtDate(r.trial_date || r.preferred_date)}
                    {r.trial_date && r.preferred_date && r.trial_date !== r.preferred_date && (
                      <div className="text-[9px] text-[#9A7B4F]/70 italic mt-0.5" title={`Customer originally entered ${r.preferred_date}; resolved from ${r.corrected_preferred_date ? "admin correction" : "scheduled appointment"}`}>
                        corrected
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-[#676664]" data-testid={`recent-converted-${r.lead_id}`}>
                    {fmtDate(r.converted_at_resolved || r.converted_at)}
                  </td>
                  <td className="py-2">
                    {r.booking_ids && r.booking_ids.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {r.booking_ids.map((bid) => {
                          const isActive = (r.active_booking_ids || []).includes(bid);
                          return (
                            <Link key={bid} to={`/admin/bookings?id=${bid}`}
                                  className={`text-[10.5px] font-mono hover:underline inline-flex items-center gap-1 ${isActive ? 'text-[#6B8F71] font-medium' : 'text-[#676664]/60 line-through'}`}
                                  title={isActive ? "Active booking — counted in revenue" : "Cancelled/refunded — excluded from revenue"}
                                  data-testid={`recent-booking-${bid}`}>
                              {bid} <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                          );
                        })}
                        {r.linking_methods && r.linking_methods.length > 0 && (
                          <div className="text-[9px] text-[#676664]/40 italic mt-0.5" title="How this trial was linked to the booking(s)">
                            via {r.linking_methods.join(", ")}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#676664]/40 italic">no linked booking</span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums text-[#282828]">{r.revenue ? AUD(r.revenue) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </>
      )}
        </>
      )}
    </AdminLayout>
  );
}
